/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Paper, Stack } from '@mui/material';
import type { PickerDayProps } from '@mui/x-date-pickers/PickerDay';
import { PickerDay } from '@mui/x-date-pickers/PickerDay';
import type { FC } from 'react';
import React from 'react';
import { dateUtils, DateTimePicker, pickerDateToUTC } from '@opengeoweb/shared';
import { useTimesliderTranslation } from '../../../utils/i18n';

export interface MarkerTimeSelectorProps {
  value?: number; // Unix timestamp in seconds
  dataStartTime?: number;
  dataEndTime?: number;
  onSelect?: (value: number | null) => void;
  onCancel?: () => void;
  format?: string;
}

interface DayStyle {
  borderWidth?: string;
  borderBottomStyle?: string;
  borderRadius?: number;
  borderColor?: string;
  backgroundColor?: string;
}

const enabledAndSelectedButtonStyle = {
  borderWidth: '6px',
  borderBottomStyle: 'solid',
  borderRadius: 0,
  borderColor: 'rgb(170, 210, 240)',
  backgroundColor: 'rgba(170, 210, 240, 0.3)',
};

export const enabledButUnSelectedButtonStyle = {
  ...enabledAndSelectedButtonStyle,
  backgroundColor: 'transparent',
};

export const disabledButtonStyle = {
  ...enabledAndSelectedButtonStyle,
  borderColor: 'transparent',
  backgroundColor: 'transparent',
};

const AvailableDayByTimeRange = (
  props: PickerDayProps,
  isDisabledStyle: boolean,
): React.ReactElement => {
  const { day, today, outsideCurrentMonth, ...other } = props;
  const [calendarDayStyle, setCalendarDayStyle] = React.useState<DayStyle>(
    {} as DayStyle,
  );

  React.useEffect(() => {
    if (outsideCurrentMonth || isDisabledStyle) {
      setCalendarDayStyle(disabledButtonStyle);
    } else if (today) {
      setCalendarDayStyle(enabledAndSelectedButtonStyle);
    } else {
      setCalendarDayStyle(enabledButUnSelectedButtonStyle);
    }
  }, [day, today, outsideCurrentMonth, isDisabledStyle]);

  return (
    <PickerDay
      {...other}
      day={day}
      disableHighlightToday={true}
      key={day.toString()}
      outsideCurrentMonth={outsideCurrentMonth}
      sx={{ ...calendarDayStyle }}
    />
  );
};

export const MarkerTimeSelector: FC<MarkerTimeSelectorProps> = ({
  value,
  format = dateUtils.DATE_FORMAT_DATEPICKER,
  dataStartTime,
  dataEndTime,
  onSelect = (): void => {},
  onCancel = (): void => {},
}) => {
  const { t } = useTimesliderTranslation();

  const [utcTimeValue, setUtcTimeValue] = React.useState<Date | null>(
    value ? dateUtils.fromUnix(value) : null,
  );

  const [isUndefinedDateRange, setIsUndefinedDateRange] =
    React.useState<boolean>(
      !dataStartTime || !dataEndTime || dataStartTime > dataEndTime,
    );

  const isDateRangeDisabled = React.useCallback(
    (date: Date | null): boolean => {
      if (
        !date ||
        dataStartTime === undefined ||
        dataEndTime === undefined ||
        dataStartTime > dataEndTime
      ) {
        return true;
      }

      const asUtcDay = (value: Date): number =>
        Date.UTC(
          value.getUTCFullYear(),
          value.getUTCMonth(),
          value.getUTCDate(),
        );
      const firstDay = asUtcDay(dateUtils.fromUnix(dataStartTime));
      const lastDay = asUtcDay(dateUtils.fromUnix(dataEndTime));
      const day = asUtcDay(date);
      return day < firstDay || day > lastDay;
    },
    [dataStartTime, dataEndTime],
  );

  const [isDisabledRange, setIsDisabledRange] = React.useState<boolean>(
    isDateRangeDisabled(utcTimeValue),
  );

  const handleSelect = (): void => {
    if (utcTimeValue) {
      onSelect(dateUtils.unix(utcTimeValue));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      handleSelect();
    } else if (event.key === 'Escape') {
      onCancel();
    }
  };

  const isDisabledDateWithSomeAvailableData = (): boolean => {
    return isUndefinedDateRange ? false : isDisabledRange;
  };

  const handleDateChange = (value: Date | null): void => {
    if (
      value instanceof Date ||
      value === null ||
      isDisabledDateWithSomeAvailableData()
    ) {
      setUtcTimeValue(value);
    }
  };

  const AvailableDay = (props: PickerDayProps): React.ReactElement => {
    return AvailableDayByTimeRange(
      props,
      isDateRangeDisabled(pickerDateToUTC(props.day)),
    );
  };

  React.useEffect(() => {
    setIsUndefinedDateRange(
      !dataStartTime || !dataEndTime || dataStartTime > dataEndTime,
    );
    setIsDisabledRange(isDateRangeDisabled(utcTimeValue));
  }, [dataStartTime, dataEndTime, utcTimeValue, isDateRangeDisabled]);

  return (
    <Paper sx={{ borderRadius: '0px', width: '240px' }}>
      <Stack data-testid="markerTimeSelector">
        <Paper
          sx={{
            height: '24px',
            align: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0px',
          }}
        >
          <Paper
            sx={{
              variant: 'subtitle',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              font: {
                fontWeight: 'medium',
                fontFamily: 'Roboto',
                fontSize: '12px',
              },

              borderRadius: '0px',
              boxShadow: 'none',
            }}
          >
            {t('timeslider-marker')}
          </Paper>
        </Paper>

        <DateTimePicker
          autoFocus={true}
          closeOnSelect={false}
          disableIgnoringDatePartForTimeValidation={true}
          format={format}
          label={t('timeslider-marker-label')}
          maxDateTime={
            isUndefinedDateRange ? undefined : dateUtils.fromUnix(dataEndTime!)
          }
          minDateTime={
            isUndefinedDateRange
              ? undefined
              : dateUtils.fromUnix(dataStartTime!)
          }
          onChange={handleDateChange}
          onClose={handleSelect}
          shouldDisableDate={isDisabledDateWithSomeAvailableData}
          slotProps={{
            field: {
              onKeyDownCapture: handleKeyDown,
            },
          }}
          slots={{
            day: AvailableDay,
          }}
          sx={{
            marginTop: '8px',
            marginBottom: '8px',
            marginLeft: '8px',
            marginRight: '8px',
            borderRadius: '0px',
          }}
          value={utcTimeValue}
        />
      </Stack>
    </Paper>
  );
};
